import { useMemo, useRef, useState } from 'react';
import { normaliseHeader, openXlsb, parseTable, readXlsx } from '../reports/engine';
import { auditService } from '../services/auditService';
import './MasterFileUploader.css';

const PREVIEW_ROWS = 15;

const FIELDS = [
  {
    key: 'ean',
    label: 'EAN / barcode',
    required: true,
    aliases: ['ean', 'barcode', 'bar code', 'ean code', 'ean no', 'ean number', 'upc', 'gtin', 'addl item code', 'additional item code', 'scan code'],
  },
  {
    key: 'article',
    label: 'Article / item code',
    aliases: ['article', 'article code', 'article no', 'item code', 'logic item code', 'sku', 'internal code', 'material', 'style code', 'product code'],
  },
  {
    key: 'description',
    label: 'Description',
    aliases: ['description', 'item description', 'product name', 'item name', 'product description', 'desc', 'item name with color', 'name'],
  },
];

const fmt = (value) => Number(value || 0).toLocaleString();
const clean = (value) => String(value ?? '').replace(/[\t\r\n]+/g, ' ').trim();
const looksLikeEan = (value) => /^\d{8,14}$/.test(clean(value));

/** Header row = first of the first rows that names an EAN column. */
function findHeader(rows) {
  const eanNames = new Set(FIELDS[0].aliases);
  for (let index = 0; index < Math.min(rows.length, PREVIEW_ROWS); index += 1) {
    if (rows[index].some((cell) => eanNames.has(normaliseHeader(cell)))) return index;
  }
  return -1;
}

/** Aliases are in order of preference: the first one found wins. */
function guessByHeader(header) {
  const names = header.map((cell) => normaliseHeader(cell));
  const mapping = {};
  FIELDS.forEach((field) => {
    let index = -1;
    for (const alias of field.aliases) {
      index = names.indexOf(alias);
      if (index >= 0) break;
    }
    mapping[field.key] = index >= 0 ? String(index) : '';
  });
  return mapping;
}

/** No header: EAN = column with the most 8-14 digit values; description = longest text. */
function guessByContent(rows) {
  const width = rows.reduce((max, row) => Math.max(max, row.length), 0);
  const score = Array.from({ length: width }, (_, col) => ({
    col,
    ean: rows.filter((row) => looksLikeEan(row[col])).length,
    text: rows.reduce((sum, row) => sum + (/[a-z]/i.test(row[col] || '') ? String(row[col]).length : 0), 0),
  }));
  const ean = [...score].sort((a, b) => b.ean - a.ean)[0];
  const description = [...score].filter((s) => s.col !== ean?.col).sort((a, b) => b.text - a.text)[0];
  // Standard 4-column master (store, article, EAN, name): article is the column before the EAN.
  const article = width >= 4 && ean?.col === 2 ? 1 : '';
  return {
    ean: ean && ean.ean ? String(ean.col) : '',
    article: article === '' ? '' : String(article),
    description: description && description.text ? String(description.col) : '',
  };
}

/** Reads .txt/.csv/.xlsx/.xlsb into sheets of text rows (also used by the adjustment upload). */
export async function readPreview(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith('.xls')) {
    throw new Error('Old .xls files are not supported. Save as .xlsx, .xlsb or CSV and try again.');
  }
  if (name.endsWith('.xlsb')) {
    const book = await openXlsb(new Uint8Array(await file.arrayBuffer()));
    const sheets = await Promise.all(
      book.sheets.map(async (sheet, index) => ({
        name: sheet.name,
        rows: await book.readSheet(index, { limit: PREVIEW_ROWS }),
      })),
    );
    return {
      kind: 'xlsb',
      sheets,
      readAll: (sheetIndex, columns) => book.readSheet(sheetIndex, { columns }),
    };
  }
  if (name.endsWith('.xlsx')) {
    const all = await readXlsx(new Uint8Array(await file.arrayBuffer()));
    const sheets = all.map((sheet) => ({
      name: sheet.name,
      full: sheet.rows.map((row) => row.cells.map((cell) => String(cell ?? ''))),
    }));
    return {
      kind: 'xlsx',
      sheets: sheets.map((sheet) => ({ name: sheet.name, rows: sheet.full.slice(0, PREVIEW_ROWS) })),
      readAll: async (sheetIndex) => sheets[sheetIndex].full,
    };
  }
  // Text: tab, comma, semicolon or pipe separated.
  const text = (await file.text()).replace(/^﻿/, '');
  const { rows } = parseTable(text);
  const full = rows.map((row) => row.cells.map((cell) => String(cell ?? '')));
  return {
    kind: 'text',
    sheets: [{ name: file.name, rows: full.slice(0, PREVIEW_ROWS) }],
    readAll: async () => full,
  };
}

/**
 * Upload an article master (AMD) in any of the client formats: the
 * standard tab-separated .txt (with or without a header), CSV, .xlsx or
 * .xlsb. The user picks which columns hold the EAN, the article code and the
 * description; the file is then sent in the standard format, in parts.
 */
function MasterFileUploader({ auditId, storeCode = '', onUploaded }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [source, setSource] = useState(null);
  const [sheetIndex, setSheetIndex] = useState(0);
  const [headerRow, setHeaderRow] = useState(-1); // -1 = no header row
  const [mapping, setMapping] = useState({ ean: '', article: '', description: '' });
  const [reading, setReading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const sheet = source?.sheets[sheetIndex];
  const previewRows = useMemo(() => sheet?.rows || [], [sheet]);

  const applyGuess = (rows) => {
    const header = findHeader(rows);
    setHeaderRow(header);
    setMapping(header >= 0 ? guessByHeader(rows[header]) : guessByContent(rows));
  };

  const choose = async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = '';
    if (!picked) return;
    setError('');
    setNote('');
    setReading(true);
    try {
      const read = await readPreview(picked);
      const first = Math.max(0, read.sheets.findIndex((s) => s.rows.length > 0));
      if (!read.sheets[first]?.rows.length) throw new Error('The file has no rows.');
      setFile(picked);
      setSource(read);
      setSheetIndex(first);
      applyGuess(read.sheets[first].rows);
    } catch (err) {
      setError(err.message);
      setFile(null);
      setSource(null);
    } finally {
      setReading(false);
    }
  };

  const columns = useMemo(() => {
    const width = previewRows.reduce((max, row) => Math.max(max, row.length), 0);
    const header = headerRow >= 0 ? previewRows[headerRow] || [] : [];
    return Array.from({ length: width }, (_, index) => {
      const sample = previewRows.find((row, rowIndex) => rowIndex > headerRow && clean(row[index]));
      return {
        index,
        label: clean(header[index]) || `Column ${index + 1}`,
        sample: sample ? clean(sample[index]).slice(0, 24) : '',
      };
    });
  }, [previewRows, headerRow]);

  const pick = (row, key) => (mapping[key] === '' ? '' : clean(row[Number(mapping[key])]));

  const preview = useMemo(
    () =>
      previewRows
        .slice(headerRow + 1)
        .filter((row) => pick(row, 'ean'))
        .slice(0, 5)
        .map((row) => ({ ean: pick(row, 'ean'), article: pick(row, 'article'), description: pick(row, 'description') })),
    // pick reads mapping
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewRows, headerRow, mapping],
  );

  const upload = async () => {
    if (!source || mapping.ean === '') return;
    setError('');
    setNote('');
    setProgress({ stage: 'read' });
    try {
      const wanted = ['ean', 'article', 'description']
        .filter((key) => mapping[key] !== '')
        .map((key) => Number(mapping[key]));
      const rows = await source.readAll(sheetIndex, wanted);
      const lines = [];
      const seen = new Set();
      let missing = 0;
      let duplicates = 0;
      let nonNumeric = 0;
      let tooLong = 0;
      let tooLongExample = '';
      rows.slice(headerRow + 1).forEach((row, index) => {
        const ean = pick(row, 'ean');
        if (!ean) {
          if (row.some((cell) => clean(cell))) missing += 1;
          return;
        }
        // The server keeps EANs up to 50 characters; longer is a broken line.
        if (ean.length > 50) {
          tooLong += 1;
          if (!tooLongExample) tooLongExample = `row ${headerRow + index + 2}: "${ean.slice(0, 30)}…"`;
          return;
        }
        if (seen.has(ean)) duplicates += 1;
        seen.add(ean);
        if (!/^\d+$/.test(ean)) nonNumeric += 1;
        const article = pick(row, 'article').slice(0, 50);
        const description = (pick(row, 'description') || article || ean).slice(0, 500);
        lines.push([clean(storeCode), article, ean, description].join('\t'));
      });
      if (!lines.length) throw new Error('No rows with an EAN in the chosen column.');

      setProgress({ stage: 'upload', part: 0, parts: 1, rows: lines.length });
      const result = await auditService.uploadMasterFile(
        auditId,
        { data: lines.join('\n'), filename: file.name },
        (p) => setProgress({ stage: 'upload', rows: lines.length, ...p }),
      );
      const parts = [
        `${fmt(lines.length)} products uploaded from ${file.name}`,
        missing ? `${fmt(missing)} rows without an EAN skipped` : '',
        duplicates ? `${fmt(duplicates)} repeated EANs` : '',
        nonNumeric ? `${fmt(nonNumeric)} EANs contain letters` : '',
        tooLong ? `${fmt(tooLong)} rows skipped because the EAN is over 50 characters (e.g. ${tooLongExample})` : '',
        result?.skipped_lines
          ? `${fmt(result.skipped_lines)} lines rejected by the server${result.warnings?.[0] ? ` (e.g. ${result.warnings[0]})` : ''}`
          : '',
      ].filter(Boolean);
      setNote(`${parts.join(' · ')}.`);
      setFile(null);
      setSource(null);
      onUploaded?.(result);
    } catch (err) {
      setError(`Master file not uploaded: ${err.message}`);
    } finally {
      setProgress(null);
    }
  };

  const busy = reading || Boolean(progress);

  return (
    <div className="master-upload">
      <div className="master-upload__row">
        <button type="button" className="master-upload__choose" onClick={() => inputRef.current?.click()} disabled={busy}>
          {reading ? 'Reading file…' : file ? `File: ${file.name}` : 'Choose master file (.txt, .csv, .xlsx, .xlsb)…'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".txt,.tsv,.csv,.xlsx,.xlsb"
          onChange={choose}
          hidden
          aria-label="Master file"
        />
        {file && !busy ? (
          <button type="button" className="master-upload__link" onClick={() => { setFile(null); setSource(null); }}>
            Cancel
          </button>
        ) : null}
      </div>

      {error ? <p className="master-upload__error">{error}</p> : null}
      {note ? <p className="master-upload__note">{note}</p> : null}

      {source && sheet ? (
        <>
          <div className="master-upload__grid">
            {source.sheets.length > 1 ? (
              <label>
                Sheet
                <select
                  value={sheetIndex}
                  onChange={(event) => {
                    const index = Number(event.target.value);
                    setSheetIndex(index);
                    applyGuess(source.sheets[index].rows);
                  }}
                  disabled={busy}
                >
                  {source.sheets.map((s, index) => (
                    <option key={s.name} value={index}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label>
              Header row
              <select
                value={headerRow}
                onChange={(event) => {
                  const row = Number(event.target.value);
                  setHeaderRow(row);
                  setMapping(row >= 0 ? guessByHeader(previewRows[row] || []) : guessByContent(previewRows));
                }}
                disabled={busy}
              >
                <option value={-1}>No header row (data starts at row 1)</option>
                {previewRows.map((row, index) => (
                  <option key={index} value={index}>
                    Row {index + 1}: {row.slice(0, 4).map((c) => clean(c) || '—').join(' | ')}
                  </option>
                ))}
              </select>
            </label>
            {FIELDS.map((field) => (
              <label key={field.key}>
                {field.label}
                {field.required ? ' *' : ''}
                <select
                  value={mapping[field.key] ?? ''}
                  onChange={(event) => setMapping((prev) => ({ ...prev, [field.key]: event.target.value }))}
                  disabled={busy}
                >
                  <option value="">{field.required ? 'Choose column…' : 'Not in this file'}</option>
                  {columns.map((column) => (
                    <option key={column.index} value={String(column.index)}>
                      {column.index + 1}: {column.label}
                      {column.sample ? ` (e.g. ${column.sample})` : ''}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          {mapping.ean === '' ? (
            <p className="master-upload__hint">Choose the column that holds the EAN / barcode.</p>
          ) : (
            <div className="master-upload__preview">
              <table>
                <thead>
                  <tr>
                    <th>EAN</th>
                    <th>Article</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, index) => (
                    <tr key={index}>
                      <td className={looksLikeEan(row.ean) ? '' : 'master-upload__warn'}>{row.ean}</td>
                      <td>{row.article || '—'}</td>
                      <td>{row.description || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!mapping.description ? (
                <p className="master-upload__hint">No description column: the article code (or EAN) is used as the name.</p>
              ) : null}
            </div>
          )}

          <div className="master-upload__row">
            <button type="button" className="master-upload__submit" onClick={upload} disabled={busy || mapping.ean === ''}>
              {!progress
                ? 'Upload master to schedule'
                : progress.stage === 'read'
                  ? 'Reading all rows…'
                  : progress.parts > 1
                    ? `Uploading part ${Math.min((progress.part || 0) + 1, progress.parts)} of ${progress.parts}…`
                    : `Uploading ${fmt(progress.rows)} products…`}
            </button>
            <span className="master-upload__hint">Replaces this schedule's master. Phones get it with “Sync AMD from Server”.</span>
          </div>
        </>
      ) : null}
    </div>
  );
}

export default MasterFileUploader;
